import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { addKlachtAction, KlachtError } from '../../../lib/klachten';
import { formatCurrency } from '../../../lib/format';
import { useKlachtDialogStore } from '../../../stores/klacht-dialog-store';

interface Props {
  onAdded?: (result: { code?: string }) => void;
}

const DEFAULT_GELDIG_DAGEN = 365;

/**
 * Action-dialog: coulance-tegoed. Genereert een Coulance_Tegoed__c-record
 * dat de klant later kan inwisselen op een nieuwe order. Apex genereert
 * automatisch een code (CT-{5char}-{6char}); we tonen die nadat het
 * record is aangemaakt.
 */
export function AddCoulanceVoucherDialog({ onAdded }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'addCoulance';
  const klachtId = payload.klachtId;

  const [bedrag, setBedrag] = useState('');
  const [geldigDagen, setGeldigDagen] = useState<string>(String(DEFAULT_GELDIG_DAGEN));
  const [toelichting, setToelichting] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setBedrag('');
      setGeldigDagen(String(DEFAULT_GELDIG_DAGEN));
      setToelichting('');
      setSubmitting(false);
      setError(null);
      setGeneratedCode(null);
    }
  }, [isOpen]);

  const bedragNum = Number(bedrag.replace(',', '.'));
  const bedragValid = Number.isFinite(bedragNum) && bedragNum > 0;
  const dagenNum = Number(geldigDagen);
  const dagenValid = Number.isFinite(dagenNum) && dagenNum > 0;

  const handleSubmit = useCallback(async () => {
    if (!klachtId) {
      setError('Geen klacht-id meegegeven');
      return;
    }
    if (!bedragValid) {
      setError('Bedrag moet groter zijn dan €0');
      return;
    }
    if (!dagenValid) {
      setError('Looptijd moet groter zijn dan 0 dagen');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await addKlachtAction({
        klachtId,
        actionType: 'CoulanceVoucher',
        params: {
          bedragInclBtw: bedragNum,
          geldigDagen: dagenNum,
          toelichting: toelichting.trim() || null,
        },
      });
      // Action-handler returnt actionRecordId — wachten op detail-refetch om
      // de echte code te tonen, maar als de result-payload het al bevat tonen.
      const possibleCode = (result as unknown as { code?: string }).code;
      if (possibleCode) setGeneratedCode(possibleCode);
      onAdded?.({ code: possibleCode });
      if (!possibleCode) close();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Tegoed aanmaken mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [klachtId, bedragNum, bedragValid, dagenNum, dagenValid, toelichting, close, onAdded]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Coulance-tegoed</h2>
          <p className="text-xs text-slate-500 mt-1">
            Tegoed dat de klant kan inzetten op een nieuwe order.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {generatedCode ? (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-3">
              <div className="font-medium">Tegoed aangemaakt</div>
              <div className="mt-1 font-mono text-base">{generatedCode}</div>
              <div className="mt-1 text-xs text-emerald-700">
                Deel deze code met de klant voor inzage bij volgende order.
              </div>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="text-sm text-slate-700">
                  Bedrag (incl. BTW) <span className="text-red-600">*</span>
                </span>
                <div className="mt-1 flex items-center border border-slate-300 rounded">
                  <span className="px-2 text-sm text-slate-500">€</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bedrag}
                    onChange={(e) => setBedrag(e.target.value)}
                    placeholder="0,00"
                    className="flex-1 px-2 py-1.5 text-sm outline-none"
                    disabled={submitting}
                  />
                </div>
                {bedragValid && (
                  <p className="mt-1 text-xs text-slate-500">{formatCurrency(bedragNum)}</p>
                )}
              </label>

              <label className="block">
                <span className="text-sm text-slate-700">
                  Geldig (dagen) <span className="text-red-600">*</span>
                </span>
                <input
                  type="number"
                  min={1}
                  value={geldigDagen}
                  onChange={(e) => setGeldigDagen(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-slate-500">Default: 365 dagen.</p>
              </label>

              <label className="block">
                <span className="text-sm text-slate-700">Toelichting</span>
                <textarea
                  value={toelichting}
                  onChange={(e) => setToelichting(e.target.value)}
                  rows={3}
                  placeholder="Bv. compensatie voor reinigingsschade"
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  disabled={submitting}
                />
              </label>

              {error && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {generatedCode ? (
            <button
              type="button"
              onClick={close}
              className="text-sm font-medium px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Klaar
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Sluiten
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !bedragValid || !dagenValid}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  submitting || !bedragValid || !dagenValid
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                )}
              >
                {submitting ? 'Bezig…' : 'Tegoed aanmaken'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
